import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { flip, offset, shift, type ReferenceType } from '@floating-ui/react';
import { invalidateAppointmentQueries } from '@/api/mcp-appointments/invalidate';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useAdminClients } from '@/contexts/AdminClientsContext';
import type { Appointment } from '@/api/mcp-appointments';
import {
  AppointmentManageOverlays,
  CreateAppointmentDialog,
  useAppointmentManageFlow,
  type CreateAppointmentDialogInitialValues,
  type CreateAppointmentDialogMetadata,
} from '@/components/appointments';

export interface AppointmentCreateSession {
  moduleId?: string;
  moduleData?: unknown;
  initialValues?: CreateAppointmentDialogInitialValues;
  metadata?: CreateAppointmentDialogMetadata;
  title?: string;
  showConfirmSwitch?: boolean;
  showResourceSelector?: boolean;
  showAppointmentType?: boolean;
  showNotes?: boolean;
  onSuccess?: (appointment: unknown) => void | Promise<void>;
  onError?: (error: Error) => void;
}

export interface AppointmentDetailsOptions {
  popupZIndex?: number;
}

interface AppointmentContextValue {
  openCreate: (session: AppointmentCreateSession) => void;
  openDetails: (
    appointment: Appointment,
    reference?: ReferenceType | null,
    options?: AppointmentDetailsOptions,
  ) => void;
  close: () => void;
  registerRescheduleWithAI: (handler: ((appointment: Appointment) => void) | null) => void;
}

const AppointmentContext = createContext<AppointmentContextValue | null>(null);

/**
 * UI orchestration for appointment dialogs and detail overlays across /manage routes.
 * Not to be confused with {@link AppointmentContextData} — module payload stored on `appointment.context`.
 */
export function AppointmentProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { payload } = useAuth();
  const { selectedClientId, selectedCompanyId } = useAdminClients();
  const [createSession, setCreateSession] = useState<AppointmentCreateSession | null>(null);
  const [detailsPopupZIndex, setDetailsPopupZIndex] = useState<number | undefined>(undefined);
  const [rescheduleWithAI, setRescheduleWithAI] = useState<((appointment: Appointment) => void) | null>(null);

  const invalidateAppointments = useCallback(async () => {
    invalidateAppointmentQueries(
      queryClient,
      payload?.role,
      selectedClientId,
      selectedCompanyId
    );
  }, [queryClient, payload?.role, selectedClientId, selectedCompanyId]);

  const appointmentManageFlow = useAppointmentManageFlow({
    enableDelete: true,
    onMutated: invalidateAppointments,
    onManualRescheduleClose: invalidateAppointments,
    floatingMiddleware: [
      offset(10),
      flip({
        fallbackPlacements: ['top-start', 'bottom-start', 'top-end', 'bottom-end', 'left', 'right'],
      }),
      shift({ padding: 16 }),
    ],
  });
  const { closeFlow } = appointmentManageFlow;
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const previousPathRef = useRef(pathname);

  useEffect(() => {
    if (previousPathRef.current === pathname) {
      return;
    }

    previousPathRef.current = pathname;
    closeFlow();
    setDetailsPopupZIndex(undefined);
  }, [closeFlow, pathname]);

  const closeCreate = useCallback(() => {
    setCreateSession(null);
  }, []);

  const close = useCallback(() => {
    closeCreate();
    appointmentManageFlow.closeFlow();
    setDetailsPopupZIndex(undefined);
  }, [appointmentManageFlow, closeCreate]);

  const openCreate = useCallback((session: AppointmentCreateSession) => {
    setCreateSession(session);
  }, []);

  const openDetails = useCallback(
    (
      appointment: Appointment,
      reference?: ReferenceType | null,
      options?: AppointmentDetailsOptions,
    ) => {
      setDetailsPopupZIndex(options?.popupZIndex);
      appointmentManageFlow.openDetails(appointment, reference);
    },
    [appointmentManageFlow],
  );

  const registerRescheduleWithAI = useCallback((handler: ((appointment: Appointment) => void) | null) => {
    setRescheduleWithAI(handler);
  }, []);

  const handleCreateSuccess = useCallback(
    async (appointment: unknown) => {
      await invalidateAppointments();
      await createSession?.onSuccess?.(appointment);
      closeCreate();
    },
    [closeCreate, createSession, invalidateAppointments],
  );

  const handleCreateError = useCallback(
    (error: Error) => {
      createSession?.onError?.(error);
    },
    [createSession],
  );

  const contextValue = useMemo(
    (): AppointmentContextValue => ({
      openCreate,
      openDetails,
      close,
      registerRescheduleWithAI,
    }),
    [close, openCreate, openDetails, registerRescheduleWithAI],
  );

  return (
    <AppointmentContext.Provider value={contextValue}>
      {children}
      <AppointmentManageOverlays
        activeAppointment={appointmentManageFlow.activeAppointment}
        detailsOpen={appointmentManageFlow.detailsOpen}
        detailsPositioned={appointmentManageFlow.detailsPositioned}
        managePhase={appointmentManageFlow.managePhase}
        pendingAppointmentId={appointmentManageFlow.pendingAppointmentId}
        floatingStyles={appointmentManageFlow.floatingStyles}
        floatingRef={appointmentManageFlow.refs.setFloating}
        popupZIndex={detailsPopupZIndex ?? 1050}
        onCloseDetails={appointmentManageFlow.closeFlow}
        onCloseFlow={appointmentManageFlow.closeFlow}
        onConfirm={appointmentManageFlow.confirmAppointment}
        onManualReschedule={appointmentManageFlow.requestManualReschedule}
        onCancel={appointmentManageFlow.cancelAppointment}
        onMarkNoShow={appointmentManageFlow.markNoShowAppointment}
        onDelete={appointmentManageFlow.deleteAppointment}
        onRescheduleWithAI={rescheduleWithAI ?? undefined}
        onCloseManualReschedule={appointmentManageFlow.closeManualReschedule}
        onSaveNotes={appointmentManageFlow.saveAppointmentNotes}
        savingNotesAppointmentId={appointmentManageFlow.savingNotesAppointmentId}
      />
      <CreateAppointmentDialog
        visible={createSession != null}
        onCancel={closeCreate}
        moduleId={createSession?.moduleId}
        moduleData={createSession?.moduleData}
        initialValues={createSession?.initialValues}
        metadata={createSession?.metadata}
        title={createSession?.title}
        showConfirmSwitch={createSession?.showConfirmSwitch}
        showResourceSelector={createSession?.showResourceSelector}
        showAppointmentType={createSession?.showAppointmentType}
        showNotes={createSession?.showNotes}
        onSuccess={handleCreateSuccess}
        onError={createSession?.onError ? handleCreateError : undefined}
      />
    </AppointmentContext.Provider>
  );
}

export function useAppointments(): AppointmentContextValue {
  const context = useContext(AppointmentContext);
  if (!context) {
    throw new Error('useAppointments must be used within AppointmentProvider');
  }
  return context;
}

/** Returns appointment UI context when inside {@link AppointmentProvider}, otherwise null. */
export function useOptionalAppointments(): AppointmentContextValue | null {
  return useContext(AppointmentContext);
}
